import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import api from '@/lib/api';

const ENV_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

/**
 * Explicit render Turnstile for SPA.
 * Site key from Super Admin → Infraestructura (runtime API), env fallback.
 *
 * IMPORTANT: parent callbacks (onToken/onExpire/onError) are stored in refs so
 * they never tear down / re-create the Cloudflare widget on every parent render
 * (inline arrows like onExpire={() => ...} would otherwise cause a flicker loop).
 *
 * The parent gets an imperative `reset()` via ref — call it after a FAILED
 * submit so Cloudflare issues a fresh single-use token (the previous one is
 * already spent, and the widget will not re-issue one on its own). Before
 * 2026-07-21 the reset was exposed as a DOM property on this component's inner
 * container, but Login/ForgotPassword looked for it on their OUTER wrapper div
 * where it never existed — so the reset silently no-op'd and, after any failed
 * attempt, users were stuck with a visually-solved widget ("¡Operación
 * exitosa!") but a null token, so the backend kept rejecting with "Falta el
 * token de verificación". Exposing it through a real ref removes that mismatch.
 */
const TurnstileWidget = forwardRef(function TurnstileWidget(
  { action = 'login', onToken, onExpire, onError, className }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbacksRef = useRef({ onToken, onExpire, onError });
  callbacksRef.current = { onToken, onExpire, onError };

  const [siteKey, setSiteKey] = useState(ENV_SITE_KEY);
  const [enabled, setEnabled] = useState(Boolean(ENV_SITE_KEY));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Imperative reset for the parent: clear the stale token in parent state,
  // then re-arm the Cloudflare widget so it produces a fresh single-use token.
  useImperativeHandle(ref, () => ({
    reset: () => {
      callbacksRef.current.onToken?.(null);
      if (widgetIdRef.current != null && window.turnstile) {
        try { window.turnstile.reset(widgetIdRef.current); } catch { /* */ }
      }
    },
  }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/auth/turnstile-config');
        if (cancelled) return;
        const key = (data?.site_key || ENV_SITE_KEY || '').trim();
        const on = Boolean(data?.enabled && key);
        setSiteKey(key);
        setEnabled(on);
      } catch {
        if (cancelled) return;
        setEnabled(Boolean(ENV_SITE_KEY));
        setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Mount Turnstile once per siteKey+action. Do NOT depend on parent callbacks.
  useEffect(() => {
    if (loading || !enabled || !siteKey) return undefined;

    let cancelled = false;
    let retryTimer = null;

    const mount = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      if (widgetIdRef.current != null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* */ }
        widgetIdRef.current = null;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        callback: (token) => callbacksRef.current.onToken?.(token),
        'expired-callback': () => {
          callbacksRef.current.onToken?.(null);
          callbacksRef.current.onExpire?.();
        },
        'error-callback': () => {
          callbacksRef.current.onToken?.(null);
          callbacksRef.current.onError?.();
        },
      });
    };

    const ensureScript = () => new Promise((resolve) => {
      if (window.turnstile) { resolve(); return; }
      const existing = document.querySelector('script[data-turnstile]');
      if (existing) {
        if (window.turnstile) { resolve(); return; }
        existing.addEventListener('load', () => resolve(), { once: true });
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.dataset.turnstile = '1';
      s.onload = () => resolve();
      document.head.appendChild(s);
    });

    ensureScript().then(() => {
      if (cancelled) return;
      // Container div mounts after loading→false; wait one frame if needed
      if (!containerRef.current) {
        retryTimer = window.setTimeout(mount, 0);
      } else {
        mount();
      }
    });

    const el = containerRef.current;

    return () => {
      cancelled = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
      if (widgetIdRef.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* */ }
        widgetIdRef.current = null;
      }
      // `el` retained only so a future change can reference the mounted node;
      // Cloudflare's own remove() above already tears down the iframe.
      void el;
    };
  }, [loading, enabled, siteKey, action]);

  if (loading) {
    return (
      <p className="text-[11px] text-[#94A3B8] text-center" data-testid="turnstile-loading">
        Cargando verificación de seguridad…
      </p>
    );
  }

  if (!enabled) {
    return null;
  }

  if (!siteKey) {
    return (
      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded p-2" data-testid="turnstile-missing-key">
        Turnstile activo pero sin clave de sitio. Configúralo en Super Admin → Infraestructura → Cloudflare Turnstile.
        {loadError ? ' (No se pudo leer la configuración del servidor.)' : ''}
      </p>
    );
  }

  return <div ref={containerRef} className={className} data-testid="turnstile-widget" data-action={action} />;
});

export default TurnstileWidget;
